import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    Label,
} from "recharts";
import { useState, useRef } from "react";
import "./charts.scss";
import Button from "../button/button";
import { MdOutlineAddChart } from "react-icons/md";

const CustomizedLabel = (props) => {
    const { x, y, width, value } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={x + width / 2}
            y={y}
            dy={-10}
            fill="#000"
            fontSize={12}
            textAnchor="middle"
        >
            {`${value}€`}
        </text>
    );
};

const formatDate = (dateString) => {
    if (!dateString) return "No disponible";

    const parts = dateString.split("/");
    if (parts.length === 3) {
        const [d, m, y] = parts;
        const day = String(Number(d)).padStart(2, "0");
        const month = String(Number(m)).padStart(2, "0");
        const year = String(Number(y));
        return `${day}/${month}/${year}`;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const fechaSolicitud = payload[0]?.payload?.fechaSolicitud;

        return (
            <div className="custom-tooltip">
                <p className="custom-tooltip__label">{`${label}`}</p>

                {payload.map((entry, index) => (
                    <p
                        key={`item-${index}`}
                        style={{ color: entry.color }}
                        className="custom-tooltip__item"
                    >
                        {`${entry.name}: ${entry.value}€`}
                    </p>
                ))}
                {fechaSolicitud && (
                    <p className="custom-tooltip__date">
                        Fecha: {formatDate(fechaSolicitud)}
                    </p>
                )}
            </div>
        );
    }

    return null;
};

const customBarColors = ["#346259", "#49897d", "#68c4b2", "#86d0c1", "#a4dcd1"];

export default function SimpleBarChart({
    data = [],
    title = "Comparativa de Tarifas",
    hasCustomizedLabels = true,
    hasTitle = false,
    xAxisKey = "nombre",
    yAxisKey = "precio",
    xAxisLabel = "Tarifas",
    yAxisLabel = "Precio (€)",
    barColors = customBarColors,
    proveedores = [],
    barSize = 48,
    barGap = 16,
    barCategoryGap = 24,
}) {
    const chartData = data.length > 0 ? data : [];

    const proveedoresActuales =
        data.length > 0
            ? Array.from(
                  new Set(
                      data.reduce((acc, item) => {
                          Object.keys(item)
                              .filter(
                                  (key) =>
                                      key !== xAxisKey &&
                                      key !== "fechaSolicitud"
                              )
                              .forEach((key) => acc.push(key));
                          return acc;
                      }, [])
                  )
              )
            : proveedores;

    const [activeProveedores, setActiveProveedores] = useState(
        proveedoresActuales.reduce((acc, proveedor) => {
            acc[proveedor] = true;
            return acc;
        }, {})
    );

    const selectBar = (dataItem) => {
        setActiveProveedores((prev) => ({
            ...prev,
            [dataItem.dataKey]: !prev[dataItem.dataKey],
        }));
    };

    const handleLegendMouseEnter = (dataItem) => {};

    const handleLegendMouseLeave = (dataItem) => {};

    const chartRef = useRef(null);

    const handleExportImage = () => {
        if (!chartRef.current) return;
        const svg = chartRef.current.querySelector("svg");
        if (!svg) return;
        const { width, height } = svg.getBoundingClientRect();
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
        const img = new Image();
        const svgBlob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(svgBlob);
        img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);
            const pngUrl = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = pngUrl;
            link.download = title ? `${title}.png` : "chart.png";
            link.click();
        };
        img.src = url;
    };

    return (
        <div className="chart-card">
            {hasTitle && <h2 className="chart-title">{title}</h2>}
            <div className="chart-button">
                <Button
                    onClick={handleExportImage}
                    variant="primary"
                    label={"Exportar como imagen"}
                    icon={<MdOutlineAddChart />}
                    size="S"
                />
            </div>

            <div className="chart-container" ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 0,
                            bottom: 10,
                        }}
                        barSize={barSize}
                        barGap={barGap}
                        barCategoryGap={barCategoryGap}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={xAxisKey}></XAxis>
                        <YAxis>
                            <Label
                                value={yAxisLabel}
                                angle={-90}
                                position="insideLeft"
                                style={{ textAnchor: "middle" }}
                            />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{
                                paddingTop: "24px",
                                fontSize: "14px",
                            }}
                            onClick={selectBar}
                            onMouseOver={handleLegendMouseEnter}
                            onMouseOut={handleLegendMouseLeave}
                        />

                        {proveedoresActuales.map((proveedor, index) => (
                            <Bar
                                key={proveedor}
                                dataKey={proveedor}
                                fill={barColors[index % barColors.length]}
                                label={
                                    hasCustomizedLabels ? (
                                        <CustomizedLabel />
                                    ) : null
                                }
                                hide={!activeProveedores[proveedor]}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
